/**
 * Image optimization utilities for better performance and modern formats
 */

export interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  loading?: "lazy" | "eager";
  decoding?: "async" | "sync" | "auto";
  sizes?: string;
  srcSet?: string;
}

/**
 * Generates responsive srcset for different screen sizes
 */
export function generateSrcSet(
  baseUrl: string,
  widths: number[] = [320, 640, 768, 1024, 1280, 1920],
): string {
  return widths.map((width) => `${baseUrl}?w=${width} ${width}w`).join(", ");
}

/**
 * Converts image URL to WebP format if supported
 */
export function getWebPUrl(url: string): string {
  // For Unsplash images, add format parameter
  if (url.includes("unsplash.com")) {
    return url.includes("?") ? `${url}&fm=webp&q=80` : `${url}?fm=webp&q=80`;
  }

  // For other images, try to convert to WebP
  return url;
}

/**
 * Converts image URL to AVIF format if supported
 */
export function getAvifUrl(url: string): string {
  // For Unsplash images, add format parameter
  if (url.includes("unsplash.com")) {
    return url.includes("?") ? `${url}&fm=avif&q=80` : `${url}?fm=avif&q=80`;
  }

  // For other images, try to convert to AVIF
  return url;
}

/**
 * Optimizes Unsplash URLs for better performance
 */
export function optimizeUnsplashUrl(
  url: string,
  width?: number,
  quality = 80,
): string {
  if (!url.includes("unsplash.com")) return url;

  const baseUrl = url.split("?")[0];
  const params = new URLSearchParams();

  if (width) params.append("w", width.toString());
  params.append("q", quality.toString());
  params.append("fm", "webp");
  params.append("auto", "format");
  params.append("fit", "crop");

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Image optimization checklist
 */
export const IMAGE_OPTIMIZATION_CHECKLIST = {
  formats: [
    "✅ WebP support for modern browsers",
    "✅ AVIF support for latest browsers",
    "✅ Fallback to original format",
    "✅ Progressive enhancement",
  ],
  performance: [
    "✅ Responsive images with srcset",
    "✅ Proper width/height attributes",
    "✅ Lazy loading for non-critical images",
    "✅ Priority loading for hero images",
  ],
  accessibility: [
    "✅ Descriptive alt text",
    "✅ Proper image dimensions",
    "✅ Semantic HTML structure",
  ],
};
